'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import type { UserAuthoritiesResponse } from '@/lib/api/user/user.types'

const SHALION_STAGING_DATA_PROVIDER_LOCAL_STORAGE_KEY = 'shalion-staging-data-provider'
export const SHALION_STAGING = 'shalion-staging' as const

type UserAuthoritiesContextType = {
  isDataProviderViewEnabled: boolean
  isShalionStaff: boolean
  useStagingDataProvider: boolean
  setUseStagingDataProvider: (value: boolean) => void
}

const UserAuthoritiesContext = createContext<UserAuthoritiesContextType | undefined>(
  undefined,
)

type UserAuthoritiesProviderProps = {
  children: React.ReactNode
  authorities?: UserAuthoritiesResponse | null
}

const UserAuthoritiesProvider: React.FC<UserAuthoritiesProviderProps> = (props) => {
  const { children, authorities } = props

  const isDataProviderViewEnabled =
    authorities?.permissions?.dataprovider?.includes('view') ?? false

  const isShalionStaff = authorities?.permissions?.platform?.includes('staff') ?? false

  const [useStagingDataProvider, setUseStagingDataProvider] = useState(false)

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof globalThis !== 'undefined') {
      const stored = localStorage.getItem(SHALION_STAGING_DATA_PROVIDER_LOCAL_STORAGE_KEY)
      if (stored === 'true') {
        setUseStagingDataProvider(true)
      }
    }
  }, [])

  const handleOnChangeUseStagingDataProvider = useCallback((value: boolean) => {
    setUseStagingDataProvider(value)
    if (typeof globalThis !== 'undefined') {
      if (value) {
        localStorage.setItem(SHALION_STAGING_DATA_PROVIDER_LOCAL_STORAGE_KEY, 'true')
      } else {
        localStorage.removeItem(SHALION_STAGING_DATA_PROVIDER_LOCAL_STORAGE_KEY)
      }
    }
  }, [])

  const value = useMemo(
    () => ({
      isDataProviderViewEnabled,
      isShalionStaff,
      useStagingDataProvider: isDataProviderViewEnabled && useStagingDataProvider,
      setUseStagingDataProvider: handleOnChangeUseStagingDataProvider,
    }),
    [
      isDataProviderViewEnabled,
      isShalionStaff,
      useStagingDataProvider,
      handleOnChangeUseStagingDataProvider,
    ],
  )

  return (
    <UserAuthoritiesContext.Provider value={value}>
      {children}
    </UserAuthoritiesContext.Provider>
  )
}

const useUserAuthorities = () => {
  const context = useContext(UserAuthoritiesContext)
  if (!context) {
    throw new Error('useUserAuthorities must be used within a UserAuthoritiesProvider')
  }
  return context
}

export { UserAuthoritiesProvider, useUserAuthorities }
